<script setup lang="ts">
import DividerLine from "@/Components/DividerLine.vue";
import BrandIcon from "@/Components/Icons/BrandIcon.vue";
import ExpandIcon from "@/Components/Nav/ExpandIcon.vue";

import NavLinkList from "@/Components/Nav/NavLinkList.vue";
import NavProfileButton from "@/Components/Nav/NavProfileButton.vue";
import { Link } from "@inertiajs/vue3";
import { computed, ref } from "vue";

const props = defineProps({
    expanded: {
        type: Boolean,
        default: false,
    },
});

const visible = defineModel("visible", {
    type: Boolean,
    default: true,
});

const emit = defineEmits(["change-expanded"]);

function hidesidenav() {
    visible.value = false;
}

const sidenavContainer = ref<HTMLDivElement>();

const navClasses = computed(() => {
    const classes = [props.expanded ? "w-56" : "w-20"];
    if (!visible.value) {
        classes.push("-translate-x-full lg:translate-x-0");
    }

    return classes.join(" ");
});
</script>

<template>
    <nav
        class="fixed h-dvh select-none"
        :class="{ 'pointer-events-none lg:pointer-events-auto': !visible }"
    >
        <!-- gray background -->
        <Transition>
            <div
                class="fixed bottom-0 left-0 right-0 top-0 grow bg-black opacity-50 lg:hidden"
                v-if="visible"
                @click="hidesidenav"
            />
        </Transition>

        <div class="fixed top-0 flex h-full lg:static">
            <!-- main side nav -->
            <div
                class="flex h-full flex-col bg-gray-100 transition-all"
                :class="navClasses"
            >
                <!-- nav top -->
                <div class="flex min-h-0 flex-1 flex-col justify-between">
                    <!-- logo container -->
                    <div class="flex flex-none items-center overflow-x-clip bg-gray-900 py-4 pl-4 pr-4">
                        <Link
                            :href="route('dashboard')"
                            class="w-full overflow-x-clip"
                        >
                            <BrandIcon class="w-40 flex-none fill-white" />
                        </Link>
                    </div>

                    <!-- Navigation Links -->
                    <div
                        class="basis-90 flex-1 overflow-y-auto overflow-x-visible border-r border-gray-300 pl-4 pr-[15px] pt-8"
                        ref="sidenavContainer"
                    >
                        <NavLinkList
                            :expanded="expanded"
                            @click-link="hidesidenav()"
                        />

                        <DividerLine class="mt-8" />

                        <div class="flex justify-end px-1">
                            <button
                                @click="emit('change-expanded', !expanded)"
                                class="rounded-full p-3 hover:bg-gray-400"
                            >
                                <ExpandIcon
                                    class="h-4 w-4 fill-white transition-all"
                                    :class="{ '-rotate-180': !expanded }"
                                />
                            </button>
                        </div>
                    </div>
                </div>

                <!-- User profile button -->
                <NavProfileButton
                    @click-link="hidesidenav()"
                    :expanded="expanded"
                />
            </div>
        </div>
    </nav>
</template>

<style scoped>
.v-enter-active,
.v-leave-active {
    transition: opacity 350ms ease;
}

.v-enter-from,
.v-leave-to {
    opacity: 0;
}
</style>
